import { EmployeeDto } from '../dtos/employee.dto';
import { LocationSummaryDto } from '../../location/dtos/location-summary.dto';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTable, MatTableModule } from '@angular/material/table';
import { ErrorService } from '../../../core/services/error.service';
import { MatDialog } from '@angular/material/dialog';
import { EmployeeService } from '../services/employee.service';
import { ManageEmployeeDialog } from '../dialogs/manage-employee/manage-employee.dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { LocationService } from '../../location/services/location.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReorderDto } from '../../../shared/dtos/reorder.dto';
import { CategoryService } from '../../category/services/category.service';
import { CategoryDto } from '../../category/dtos/category.dto';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../../environments/environment';
import { MemoryService } from '../../../core/services/memory.service';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-employees',
  imports: [MatMenuModule, MatCardModule, MatButtonModule, MatTableModule, MatIconModule, CdkDropList, CdkDrag, MatFormFieldModule, ReactiveFormsModule, MatSelectModule, TranslocoPipe, MatMenuModule],
  templateUrl: './employees.component.html',
  styleUrl: './employees.component.scss'
})
export class EmployeesComponent implements OnInit {
  @ViewChild('table') table!: MatTable<any>;
  employeeDtos!: EmployeeDto[];
  locationSummaryDtos!: LocationSummaryDto[];
  categoryDtos!: CategoryDto[];

  locationFilters!: number[];
  locationFilterFormControl!: FormControl;

  filteredEmployees!: EmployeeDto[];

  constructor(private translocoService: TranslocoService, private memoryService: MemoryService, private employeeService: EmployeeService, private errorService: ErrorService, private dialog: MatDialog, private locationService: LocationService, private snackBar: MatSnackBar, private categoryService: CategoryService) {
  }

  ngOnInit(): void {
    this.categoryService.getCategories().subscribe({
      next: (categoryDtos: CategoryDto[]) => {
        this.categoryDtos = categoryDtos;
        this.loadLocations();
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.categories'));
      }
    });
  }

  loadLocations(): void {
    this.locationService.getLocationSummaries().subscribe({
      next: (locationSummaryDtos: LocationSummaryDto[]) => {
        this.locationSummaryDtos = locationSummaryDtos;
        this.locationFilters = [0, ...this.locationSummaryDtos.map((locationSummaryDto) => locationSummaryDto.locationId)];
        this.locationFilterFormControl = new FormControl(0);

        this.loadEmployees();
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.locations'));
      }
    });
  }

  loadEmployees(): void {
    this.employeeService.getEmployees().subscribe({
      next: (employeeDtos: EmployeeDto[]) => {
        this.employeeDtos = employeeDtos;

        this.filterEmployees(this.locationFilterFormControl.value);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.employees'));
      }
    });
  }

  filterEmployees(filter: number): void {
    if (filter == 0) {
      this.filteredEmployees = this.employeeDtos;
      return;
    }

    this.filteredEmployees = this.employeeDtos.filter((employeeDto) => employeeDto.locationIds?.includes(filter));
  }

  getEmployeeLocations(employee: EmployeeDto): LocationSummaryDto[] {
    if (!employee.locationIds || employee.locationIds.length === 0) {
      return [];
    }

    return this.locationSummaryDtos.filter((dto) => employee.locationIds?.includes(dto.locationId));
  }

  add(): void {
    this.dialog
      .open(ManageEmployeeDialog, {
        autoFocus: false,
        data: { locationSummaryDtos: this.locationSummaryDtos, categoryDtos: this.categoryDtos }
      })
      .afterClosed()
      .subscribe((reload) => {
        if (reload) {
          this.loadEmployees();
        }
      });
  }

  edit(employeeDto: EmployeeDto): void {
    this.dialog
      .open(ManageEmployeeDialog, {
        autoFocus: false,
        data: {
          employeeDto: employeeDto,
          locationSummaryDtos: this.locationSummaryDtos,
          categoryDtos: this.categoryDtos
        }
      })
      .afterClosed()
      .subscribe((reload) => {
        if (reload) {
          this.loadEmployees();
        }
      });
  }

  delete(employeeDto: EmployeeDto): void {
    this.employeeService.deleteEmployee(employeeDto.employeeId).subscribe({
      next: () => {
        this.loadEmployees();
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate('error.title.delete_employee'));
      }
    });
  }

  reorder(event: CdkDragDrop<string>): void {
    let employeeDto: EmployeeDto = event.item.data;
    let previousIndex: number = event.previousIndex;
    let newIndex: number = event.currentIndex;

    if (previousIndex === newIndex) {
      return;
    }

    let previousEmployeeDto: EmployeeDto | null = newIndex === 0 ? null : this.filteredEmployees.filter((_, index) => index !== previousIndex)[newIndex - 1];

    let reorderDto: ReorderDto = { objectId: employeeDto.employeeId, previousObjectId: previousEmployeeDto?.employeeId ?? null };

    let filteredEmployeesCopy = [...this.filteredEmployees];
    const element = this.filteredEmployees.splice(previousIndex, 1)[0];
    this.filteredEmployees.splice(newIndex, 0, element);
    this.table.renderRows();

    this.employeeService.reorderEmployees(reorderDto).subscribe({
      next: () => {
        this.loadEmployees();
      },
      error: (error) => {
        this.filteredEmployees = filteredEmployeesCopy;
        this.errorService.handleError(error, this.translocoService.translate('error.title.reorder_employees'));
      }
    });
  }

  changeFilter(filter: number): void {
    this.filterEmployees(filter);
  }

  locationName(locationId: number): string {
    if (locationId == 0) {
      return this.translocoService.translate('label.location.all');
    }

    let locationSummaryDto = this.locationSummaryDtos.find((locationPreviewDto) => locationPreviewDto.locationId == locationId);
    return `${locationSummaryDto?.name} - ${locationSummaryDto?.city}, ${locationSummaryDto?.street}`;
  }

  copyUrl(locationId: number, employeeId: number) {
    const url = `${environment.cenplexBookingWebUrl}/appointment-selection?customerId=${this.memoryService.customerId}&locationId=${locationId}&employeeId=${employeeId}`;

    navigator.clipboard.writeText(url).then(() => {
      this.snackBar.open(this.translocoService.translate('label.snackbar'), 'OK', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'bottom'
      });
    });
  }
}
